import { Component, HostListener, OnInit, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, debounceTime } from 'rxjs';
import { MainLayoutComponent } from '../../_layouts/main-layout/main-layout.component';
import { AlertService } from '../../_services/alert.service';
import { TradeRequestService } from '../../_services/trade-request.service';
import { TradeRequest, TradeRequestStats } from '../../_interfaces/trade-request';
import { RejectConfirmationComponent } from './reject-confirmation/reject-confirmation.component';
import { ConfirmDialogComponent } from '../../../Shared/_components/confirm-dialog/confirm-dialog.component';
import { PaginatorPagesDirective } from '../../../Shared/_directives/paginator-pages.directive';

// A request waiting longer than this is flagged.
const SLOW_AFTER_DAYS = 7;

@Component({
    selector: 'app-trade-requests',
    imports: [PaginatorPagesDirective, CommonModule, MainLayoutComponent, DatePipe, FormsModule, MatIconModule, MatTooltipModule, MatPaginatorModule, MatProgressSpinnerModule],
    templateUrl: './trade-requests.component.html',
    styleUrl: './trade-requests.component.css'
})
export class TradeRequestsComponent implements OnInit {
  private readonly dialog = inject(MatDialog);
  private readonly tradeRequestService = inject(TradeRequestService);
  private readonly alertService = inject(AlertService);

  tradeRequests: TradeRequest[] = [];
  count = 0;
  stats: TradeRequestStats | null = null;
  selectedType = 'ALL';
  traderKeyword = '';
  sort = 'default';
  pageNumber = 0;
  itemsPerPage = 10;
  loading = true;
  loadError = false;
  busyId = 0;

  // The slide-over panel: the full application, and its document's link.
  selected: TradeRequest | null = null;
  docUrl = '';
  docLoading = false;

  readonly statusOptions = [
    { value: 'ALL', label: 'All' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'REJECTED', label: 'Rejected' },
  ];

  private readonly search$ = new Subject<void>();
  private loadSeq = 0;

  constructor() {
    this.search$.pipe(debounceTime(350), takeUntilDestroyed()).subscribe(() => {
      this.pageNumber = 0;
      this.load();
    });
  }

  ngOnInit(): void {
    this.load();
  }

  get filtersActive(): boolean {
    return this.selectedType !== 'ALL' || !!this.traderKeyword;
  }

  countFor(value: string): number {
    const s = this.stats;
    if (!s) return 0;
    switch (value) {
      case 'PENDING': return s.pending;
      case 'APPROVED': return s.approved;
      case 'REJECTED': return s.rejected;
      default: return s.total;
    }
  }

  get oldestPendingDays(): number {
    return this.stats?.oldestPending ? this.daysSince(this.stats.oldestPending) : 0;
  }

  load(): void {
    const seq = ++this.loadSeq;
    this.loading = true;
    this.tradeRequestService
      .getAllTradeRequests({
        pageSize: this.itemsPerPage,
        pageIndex: this.pageNumber,
        status: this.selectedType !== 'ALL' ? this.selectedType : null,
        keyword: this.traderKeyword.trim(),
        sort: this.sort === 'default' ? undefined : this.sort,
      })
      .subscribe({
        next: (response: any) => {
          if (seq !== this.loadSeq) return;
          this.tradeRequests = response.requests ?? [];
          this.count = response.count ?? 0;
          if (response.stats) this.stats = response.stats;
          this.loading = false;
          this.loadError = false;
        },
        error: (error) => {
          if (seq !== this.loadSeq) return;
          this.loading = false;
          this.loadError = true;
          this.alertService.alert(error.error?.message || 'Could not load trade requests', 'error');
        },
      });
  }

  onSearchInput(): void {
    this.search$.next();
  }

  clearSearch(): void {
    this.traderKeyword = '';
    this.pageNumber = 0;
    this.load();
  }

  setType(value: string): void {
    this.selectedType = value;
    this.pageNumber = 0;
    this.load();
  }

  setSort(value: string): void {
    this.sort = value;
    this.pageNumber = 0;
    this.load();
  }

  resetFilters(): void {
    this.selectedType = 'ALL';
    this.traderKeyword = '';
    this.pageNumber = 0;
    this.load();
  }

  onPage(event: PageEvent): void {
    if (this.itemsPerPage !== event.pageSize) {
      this.itemsPerPage = event.pageSize || 10;
      this.pageNumber = 0;
    } else {
      this.pageNumber = event.pageIndex;
    }
    this.load();
  }

  // ---- What a row shows ----

  initials(r: TradeRequest): string {
    return `${(r.firstName ?? '').trim().charAt(0)}${(r.lastName ?? '').trim().charAt(0)}`.toUpperCase() || '?';
  }

  fullName(r: TradeRequest): string {
    return `${r.firstName ?? ''} ${r.lastName ?? ''}`.trim();
  }

  daysSince(date: string | Date): number {
    return Math.max(0, Math.floor((Date.now() - new Date(date).getTime()) / 86400000));
  }

  waiting(r: TradeRequest): string {
    const d = this.daysSince(r.reqDate);
    return d === 0 ? 'today' : d === 1 ? '1 day' : `${d} days`;
  }

  isSlow(r: TradeRequest): boolean {
    return r.status === 'PENDING' && this.daysSince(r.reqDate) > SLOW_AFTER_DAYS;
  }

  statusStyle(status: string): { label: string; cls: string; dot: string } {
    switch (status) {
      case 'APPROVED': return { label: 'Approved', cls: 'bg-emerald-50 text-emerald-700', dot: 'bg-emerald-500' };
      case 'REJECTED': return { label: 'Rejected', cls: 'bg-rose-50 text-rose-700', dot: 'bg-rose-500' };
      default: return { label: 'Pending', cls: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500' };
    }
  }

  // Websites are typed in by applicants; make sure the link is followable.
  link(url: string | undefined): string {
    if (!url || url === '-') return '';
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
  }

  shortLink(url: string | undefined): string {
    return (url ?? '').replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '');
  }

  // ---- View panel ----

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.selected = null;
  }

  // Requests without a document have "-" (or nothing) stored as the path.
  hasDocument(r: TradeRequest): boolean {
    return !!r.filePath && r.filePath !== '-';
  }

  onView(r: TradeRequest): void {
    if (this.selected?.id === r.id) {
      this.selected = null;
      return;
    }
    this.selected = r;
    this.docUrl = '';
    if (!this.hasDocument(r)) return;
    this.docLoading = true;
    this.tradeRequestService.getPresignedSignedUrl(r.filePath!).subscribe({
      next: (response: any) => {
        if (this.selected?.id !== r.id) return;
        this.docUrl = response.url;
        this.docLoading = false;
      },
      error: (error) => {
        this.docLoading = false;
        this.alertService.alert(error.error?.message || 'Could not get the document link', 'error');
      },
    });
  }

  // How the decision went, for the panel's timeline.
  waitedDays(r: TradeRequest): number {
    return this.daysSince(r.reqDate);
  }

  // ---- Actions ----

  onApprove(r: TradeRequest): void {
    this.dialog
      .open(ConfirmDialogComponent, {
        width: '460px',
        maxWidth: '96vw',
        data: {
          title: 'Approve trade request',
          message: `Approve ${this.fullName(r)} (${r.email})?`,
          detail: 'A trade account is created for this email address.',
          tone: 'success',
          confirmText: 'Approve',
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result?.confirmation) this.approve(r);
      });
  }

  onReject(r: TradeRequest): void {
    this.dialog
      .open(RejectConfirmationComponent, { width: '560px', maxWidth: '96vw', data: { name: this.fullName(r), email: r.email } })
      .afterClosed()
      .subscribe((result) => {
        if (result?.rejectReason) this.reject(r, result.rejectReason);
      });
  }

  private approve(r: TradeRequest): void {
    this.busyId = r.id;
    this.tradeRequestService.approveTradeRequest(r).subscribe({
      next: () => {
        this.busyId = 0;
        this.alertService.alert('Trade request approved!', 'success');
        if (this.selected?.id === r.id) this.selected = null;
        this.load();
      },
      error: (error) => {
        this.busyId = 0;
        this.alertService.alert(error.error?.message || 'Could not approve the request', 'error');
      },
    });
  }

  private reject(r: TradeRequest, reason: string): void {
    this.busyId = r.id;
    this.tradeRequestService.rejectTradeRequest(r.id, reason).subscribe({
      next: () => {
        this.busyId = 0;
        this.alertService.alert('Trade request rejected - the applicant has been emailed the reason', 'success');
        if (this.selected?.id === r.id) this.selected = null;
        this.load();
      },
      error: (error) => {
        this.busyId = 0;
        this.alertService.alert(error.error?.message || 'Could not reject the request', 'error');
      },
    });
  }
}
